/**
 * AdminTrafficStats — dashboard vizite din pixel tracking.
 *
 * Afişează:
 *  - 3 KPI-uri sus (total_events, unique_visitors, ratio authenticated)
 *  - Bar chart de vizite pe zi (SVG minimal, fără library)
 *  - Top 10 pagini + top evenimente
 *
 * Endpoint: GET /api/admin/tracking/stats?days=N
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Activity, Users as UsersIcon, ShieldCheck,
  MousePointerClick, Zap, Send, LayoutList,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const RANGE_OPTIONS = [
  { value: "1",  label: "Ultimele 24h" },
  { value: "7",  label: "Ultima săptămână" },
  { value: "30", label: "Ultimele 30 zile" },
  { value: "90", label: "Ultimele 90 zile" },
];

// iter 95.1 · Funnel de conversie business — 5 pași cheie
// Fiecare pas caută un eveniment concret în by_event.
const FUNNEL_STEPS = [
  { key: "pageview",       label: "Vizite site",           color: "#71717a" },
  { key: "login.success",  label: "Login-uri reușite",     color: "#8b5cf6" },
  { key: "render.start",   label: "Randări începute",      color: "#3b82f6" },
  { key: "render.submitted", label: "Randări trimise",     color: "#10b981" },
  { key: "reco.sent",      label: "Recomandări trimise",   color: "#f97316" },
];

// Categorii pentru colorat evenimentele în top-list
const EVENT_ICONS = {
  "pageview":                { icon: LayoutList,       color: "text-zinc-400"    },
  "login.success":           { icon: ShieldCheck,      color: "text-purple-400"  },
  "render.start":            { icon: Zap,              color: "text-blue-400"    },
  "render.submitted":        { icon: Zap,              color: "text-emerald-400" },
  "render.error":            { icon: Zap,              color: "text-red-400"     },
  "reco.sent":               { icon: Send,             color: "text-orange-400"  },
  "vision.profile.save":     { icon: MousePointerClick, color: "text-cyan-400"   },
  "vision.feedback.submit":  { icon: MousePointerClick, color: "text-cyan-400"   },
  "pdf.download.ghid_sales": { icon: MousePointerClick, color: "text-amber-400"  },
  "admin.perm.change":       { icon: ShieldCheck,      color: "text-emerald-400" },
};

export default function AdminTrafficStats() {
  const [range, setRange] = useState("30");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/tracking/stats?days=${range}`);
      setStats(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nu am putut încărca statisticile.");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  // Compute chart scale
  const maxDayCount = useMemo(() => {
    if (!stats?.by_day?.length) return 1;
    return Math.max(...stats.by_day.map((d) => d.count));
  }, [stats]);

  // iter 95.1 · funnel de conversie business
  const funnelData = useMemo(() => {
    if (!stats?.by_event) return null;
    const eventCount = Object.fromEntries(
      stats.by_event.map((r) => [r.event, r.count]),
    );
    const rows = FUNNEL_STEPS.map((s) => ({
      ...s,
      count: eventCount[s.key] || 0,
    }));
    const maxVal = Math.max(1, ...rows.map((r) => r.count));
    return { rows, maxVal };
  }, [stats]);

  return (
    <div className="space-y-6" data-testid="admin-traffic-stats">
      {/* Header + range picker */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Activity size={18} className="text-orange-400" />
            Trafic & Vizite
          </h3>
          <p className="text-xs text-zinc-500">
            Pixel tracker activ pe toate paginile aplicaţiei — vizite anonimizate (IP hash-uit).
          </p>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger
            className="w-48 h-9 bg-zinc-900 border-white/10 text-white text-xs"
            data-testid="traffic-range-trigger"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-white">
            {RANGE_OPTIONS.map((r) => (
              <SelectItem key={r.value} value={r.value} className="text-xs">
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading || !stats ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-orange-400" />
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <KpiCard
              icon={<Activity size={16} />}
              label="Total vizite"
              value={stats.total_events.toLocaleString("ro-RO")}
              testid="kpi-total-events"
            />
            <KpiCard
              icon={<UsersIcon size={16} />}
              label="Vizitatori unici"
              value={stats.unique_visitors.toLocaleString("ro-RO")}
              testid="kpi-unique-visitors"
            />
            <KpiCard
              icon={<ShieldCheck size={16} />}
              label="Vizite autentificate"
              value={`${(stats.authenticated_ratio * 100).toFixed(1)}%`}
              hint={`${stats.authenticated_events} din ${stats.total_events}`}
              testid="kpi-auth-ratio"
            />
          </div>

          {/* Chart pe zi */}
          <div className="border border-white/5 rounded-lg bg-zinc-900/40 p-4">
            <div className="text-xs uppercase tracking-[0.15em] text-orange-400 mb-3">
              Vizite pe zi ({stats.by_day.length} zile cu date)
            </div>
            {stats.by_day.length === 0 ? (
              <p className="text-sm text-zinc-500 py-8 text-center">
                Nu există vizite înregistrate în perioada selectată.
              </p>
            ) : (
              <div className="flex items-end gap-1 h-32">
                {stats.by_day.map((d) => (
                  <div
                    key={d.date}
                    className="flex-1 flex flex-col items-center gap-1 group"
                    data-testid={`traffic-day-${d.date}`}
                  >
                    <div
                      className="w-full bg-orange-500/60 hover:bg-orange-400 transition-colors rounded-t relative"
                      style={{ height: `${(d.count / maxDayCount) * 100}%`, minHeight: "3px" }}
                    >
                      <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-orange-300 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                        {d.count}
                      </span>
                    </div>
                    <span className="text-[9px] text-zinc-500 truncate w-full text-center">
                      {d.date.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* iter 95.1 · Funnel de conversie business */}
          {funnelData && (
            <div className="border border-white/5 rounded-lg bg-zinc-900/40 p-4" data-testid="traffic-funnel">
              <div className="text-xs uppercase tracking-[0.15em] text-orange-400 mb-3">
                Funnel de conversie — pași cheie business
              </div>
              <div className="space-y-2">
                {funnelData.rows.map((row, i) => {
                  const prev = i > 0 ? funnelData.rows[i - 1].count : row.count;
                  const conversionPct = prev > 0 ? ((row.count / prev) * 100).toFixed(1) : "—";
                  const widthPct = (row.count / funnelData.maxVal) * 100;
                  return (
                    <div key={row.key} data-testid={`funnel-step-${row.key}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-zinc-300 flex items-center gap-2">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: row.color }}
                          />
                          {row.label}
                          <code className="text-[9px] text-zinc-500">{row.key}</code>
                        </span>
                        <span className="text-white font-mono">
                          {row.count.toLocaleString("ro-RO")}
                          {i > 0 && prev > 0 && (
                            <span className="ml-2 text-[10px] text-zinc-500">
                              ({conversionPct}% vs. pasul precedent)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded overflow-hidden">
                        <div
                          className="h-full transition-all"
                          style={{ width: `${widthPct}%`, backgroundColor: row.color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-zinc-500 mt-3">
                * Procentele calculează conversia relativă la pasul precedent din funnel.
              </p>
            </div>
          )}

          {/* Top pagini + evenimente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RankList
              title="Top 10 pagini"
              rows={stats.by_page.map((r) => ({ label: r.page || "(unknown)", count: r.count }))}
              testid="traffic-top-pages"
            />
            <RankList
              title="Tipuri de evenimente"
              rows={stats.by_event.map((r) => ({
                label: r.event,
                count: r.count,
                icon: EVENT_ICONS[r.event]?.icon,
                color: EVENT_ICONS[r.event]?.color,
              }))}
              testid="traffic-events"
            />
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, hint, testid }) {
  return (
    <div
      className="border border-white/5 rounded-lg bg-zinc-900/40 p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">
        <span className="text-orange-400">{icon}</span>
        {label}
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function RankList({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-white/5 rounded-lg bg-zinc-900/40 p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.15em] text-orange-400 mb-3">{title}</div>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">Fără date.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => {
            const Icon = r.icon;
            return (
              <li key={`${r.label}-${i}`} className="text-xs">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-zinc-300 truncate flex-1 flex items-center gap-1.5">
                    {Icon && <Icon size={12} className={r.color || "text-zinc-400"} />}
                    <span className="truncate">{r.label}</span>
                  </span>
                  <span className="text-orange-400 font-mono">{r.count}</span>
                </div>
                <div className="h-1 bg-zinc-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-orange-500/60"
                    style={{ width: `${(r.count / max) * 100}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
